import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import axios from 'axios';
import Breadcrumb from '../components/Breadcrumb';
import { 
  Edit3, 
  Save, 
  X, 
  Settings
} from 'lucide-react';

interface UserProfile {
  id: string;
  email: string;
  name: string;
  city: string;
  created_at: string;
}

interface UserStats {
  user_id: string;
  total_ratings: number;
  average_rating: number;
  total_exchanges: number;
  trust_score: number;
  badges: string;
  last_updated: string;
}

const Profile: React.FC = () => {
  const { user } = useAuth();
  const { showSuccess, showError, showSettings } = useToast();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [userStats, setUserStats] = useState<UserStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    city: ''
  });
  const [availabilitySettings, setAvailabilitySettings] = useState({
    is_available: true,
    auto_accept: false,
    max_distance: 50,
    preferred_duration: 14
  });
  const [isEditingAvailability, setIsEditingAvailability] = useState(false);
  const [quota, setQuota] = useState<{ plan: string; used: number; canBorrow: boolean } | null>(null);

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    setLoading(true);
    
    try {
      const [profileResponse, statsResponse, availabilityResponse, quotaResponse] = await Promise.all([
        axios.get('/api/users/profile'),
        axios.get(`/api/ratings/user/${user?.id}/stats`),
        axios.get('/api/availability/settings'),
        axios.get('/api/users/borrow-quota').catch(() => ({ data: null }))
      ]);
      setQuota(quotaResponse.data);
      
      setProfile(profileResponse.data);
      setUserStats(statsResponse.data);
      setFormData({
        name: profileResponse.data.name,
        city: profileResponse.data.city
      });
      setAvailabilitySettings(availabilityResponse.data);
    } catch (error: any) {
      showError('Erreur', 'Erreur lors du chargement du profil');
      console.error('Fetch profile error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      await axios.patch('/api/users/profile', formData);
      showSuccess('Profil mis à jour', 'Vos informations ont été sauvegardées');
      setIsEditing(false);
      fetchProfile();
    } catch (error: any) {
      showError('Erreur', error.response?.data?.error || 'Erreur lors de la mise à jour du profil');
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleAvailabilityChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setAvailabilitySettings({
      ...availabilitySettings,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : 
              type === 'number' ? parseInt(value) : value
    });
  };

  const handleAvailabilitySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      await axios.patch('/api/availability/settings', availabilitySettings);
      showSettings('Paramètres mis à jour', 'Vos préférences ont été sauvegardées');
      setIsEditingAvailability(false);
    } catch (error: any) {
      showError('Erreur', error.response?.data?.error || 'Erreur lors de la mise à jour des paramètres');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-paper flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-stone-200 border-t-forest-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-paper">
      <Breadcrumb items={[{ label: 'Profil' }]} />
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-serif text-3xl text-ink">Mon profil</h1>
          <p className="text-ink-muted mt-1">Coordonnées, offre et disponibilités</p>
        </div>

        {quota && (
          <div className="mb-6 rounded-xl border border-gray-200 bg-white p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <p className="text-xs font-semibold tracking-widest uppercase text-forest-700">Offre actuelle</p>
              <p className="font-serif text-2xl text-ink mt-1">
                {quota.plan === 'free' ? 'Découverte' : quota.plan === 'lecteur' ? 'Lecteur' : 'Collection'}
              </p>
              <p className="text-sm text-ink-muted mt-1">
                {quota.plan === 'free'
                  ? `${quota.used}/1 emprunt d’essai utilisé`
                  : 'Emprunts selon le plafond de l’offre'}
              </p>
            </div>
            {quota.plan === 'free' && (
              <a href="/#tarifs" className="btn btn-primary whitespace-nowrap">
                Voir Lecteur 3,90 €
              </a>
            )}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Profile Info */}
          <div className="lg:col-span-2 space-y-6">
            {/* Personal Information */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-gray-900">Informations personnelles</h2>
                  <button
                    onClick={() => setIsEditing(!isEditing)}
                    className="text-forest-700 hover:text-forest-800 transition-colors"
                  >
                    {isEditing ? <X className="h-5 w-5" /> : <Edit3 className="h-5 w-5" />}
                  </button>
                </div>
              </div>
              <div className="p-6">
                {isEditing ? (
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Nom complet
                        </label>
                        <input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-forest-600 focus:border-forest-600"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Ville
                        </label>
                        <input
                          type="text"
                          name="city"
                          value={formData.city}
                          onChange={handleChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-forest-600 focus:border-forest-600"
                          required
                        />
                      </div>
                    </div>
                    <div className="flex justify-end space-x-3">
                      <button
                        type="button"
                        onClick={() => setIsEditing(false)}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                      >
                        Annuler
                      </button>
                      <button
                        type="submit"
                        className="px-4 py-2 text-sm font-medium text-white bg-forest-600 hover:bg-forest-700 rounded-lg transition-colors flex items-center"
                      >
                        <Save className="h-4 w-4 mr-2" />
                        Sauvegarder
                      </button>
                    </div>
                  </form>
                ) : (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">Nom</span>
                      <span className="font-medium text-gray-900">{profile?.name}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">Email</span>
                      <span className="font-medium text-gray-900">{profile?.email}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">Ville</span>
                      <span className="font-medium text-gray-900">{profile?.city}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">Membre depuis</span>
                      <span className="font-medium text-gray-900">
                        {profile?.created_at ? formatDate(profile.created_at) : 'N/A'}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Quick Stats */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900">Activité</h2>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-forest-700">
                      {userStats?.total_exchanges || 0}
                    </div>
                    <div className="text-sm text-gray-600">Échanges</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-yellow-600">
                      {userStats?.total_ratings || 0}
                    </div>
                    <div className="text-sm text-gray-600">Avis reçus</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Availability Settings */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-gray-900">Disponibilité</h2>
                  <button
                    onClick={() => setIsEditingAvailability(!isEditingAvailability)}
                    className="text-forest-700 hover:text-forest-800 transition-colors"
                  >
                    {isEditingAvailability ? <X className="h-5 w-5" /> : <Settings className="h-5 w-5" />}
                  </button>
                </div>
              </div>
              <div className="p-6">
                {isEditingAvailability ? (
                  <form onSubmit={handleAvailabilitySubmit} className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          name="is_available"
                          checked={availabilitySettings.is_available}
                          onChange={handleAvailabilityChange}
                          className="h-4 w-4 text-forest-700 focus:ring-forest-600 border-gray-300 rounded"
                        />
                        <label className="ml-2 text-sm text-gray-700">
                          Accepter de nouveaux échanges
                        </label>
                      </div>

                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          name="auto_accept"
                          checked={availabilitySettings.auto_accept}
                          onChange={handleAvailabilityChange}
                          className="h-4 w-4 text-forest-700 focus:ring-forest-600 border-gray-300 rounded"
                        />
                        <label className="ml-2 text-sm text-gray-700">
                          Acceptation automatique
                        </label>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Distance max (km)
                        </label>
                        <input
                          type="number"
                          name="max_distance"
                          value={availabilitySettings.max_distance}
                          onChange={handleAvailabilityChange}
                          min="1"
                          max="100"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-forest-600 focus:border-forest-600"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Durée préférée (jours)
                        </label>
                        <select
                          name="preferred_duration"
                          value={availabilitySettings.preferred_duration}
                          onChange={handleAvailabilityChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-forest-600 focus:border-forest-600"
                        >
                          <option value={7}>7 jours</option>
                          <option value={14}>14 jours</option>
                          <option value={21}>21 jours</option>
                          <option value={30}>30 jours</option>
                        </select>
                      </div>
                    </div>

                    <div className="flex justify-end space-x-3 pt-4">
                      <button
                        type="button"
                        onClick={() => setIsEditingAvailability(false)}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                      >
                        Annuler
                      </button>
                      <button
                        type="submit"
                        className="px-4 py-2 text-sm font-medium text-white bg-forest-600 hover:bg-forest-700 rounded-lg transition-colors flex items-center"
                      >
                        <Save className="h-4 w-4 mr-2" />
                        Sauvegarder
                      </button>
                    </div>
                  </form>
                ) : (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Disponibilité</span>
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        availabilitySettings.is_available 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {availabilitySettings.is_available ? 'Disponible' : 'Indisponible'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Acceptation auto</span>
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        availabilitySettings.auto_accept 
                          ? 'bg-forest-100 text-forest-800' 
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {availabilitySettings.auto_accept ? 'Activée' : 'Désactivée'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Distance max</span>
                      <span className="text-sm text-gray-900">{availabilitySettings.max_distance} km</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Durée préférée</span>
                      <span className="text-sm text-gray-900">{availabilitySettings.preferred_duration} jours</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right Column - Reputation */}
          <div className="space-y-6">
            {/* Reputation Card */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900">Réputation</h2>
              </div>
              <div className="p-6">
                {userStats && (
                  <div className="space-y-4">
                    {/* Note moyenne */}
                    <div className="text-center">
                      <div className="text-3xl font-bold text-gray-900 mb-1">
                        {userStats.average_rating ? userStats.average_rating.toFixed(1) : '0.0'}
                      </div>
                      <div className="text-sm text-gray-600">
                        {userStats.total_ratings} avis
                      </div>
                    </div>

                    {/* Score de confiance */}
                    <div className="text-center">
                      <div className="text-2xl font-bold text-green-600 mb-1">
                        {userStats.trust_score}%
                      </div>
                      <div className="text-sm text-gray-600">Score de confiance</div>
                    </div>

                    {/* Badges */}
                    {userStats.badges && userStats.badges.split(',').length > 0 && (
                      <div>
                        <div className="text-sm font-medium text-gray-700 mb-2">Badges</div>
                        <div className="flex flex-wrap gap-2">
                          {userStats.badges.split(',').map((badge, index) => (
                            <span
                              key={index}
                              className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-700 rounded-full"
                            >
                              {badge.trim()}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;